import type { Breadcrumb, Event } from '@sentry/browser';

type SentryRequest = NonNullable<Event['request']>;
type QueryParams = NonNullable<SentryRequest['query_string']>;
type SentrySpan = NonNullable<Event['spans']>[number];

const CAPABILITY_TOKEN_PARAMETER = 'token';
const OUTGOING_REQUEST_BREADCRUMB_CATEGORIES = ['http', 'xhr', 'fetch'];
const NAVIGATION_BREADCRUMB_CATEGORY = 'navigation';
const OUTGOING_REQUEST_SPAN_OPERATION = 'http.client';
const CREDENTIAL_HEADERS = ['authorization', 'cookie'];
const REFERRER_HEADER = 'referer';
const PATIENT_DATA_REQUEST_FIELDS = ['data', 'cookies'];

function splitAtFirst(
  text: string,
  separator: string,
): [string, string | undefined] {
  const index = text.indexOf(separator);
  return index === -1
    ? [text, undefined]
    : [text.slice(0, index), text.slice(index + 1)];
}

function withoutQueryString(url: string): string {
  return url.split(/[?#]/, 1)[0];
}

function withoutTokenInQueryString(queryString: string): string {
  const parameters = new URLSearchParams(queryString);
  parameters.delete(CAPABILITY_TOKEN_PARAMETER);
  return parameters.toString();
}

function withoutTokenInUrl(url: string): string {
  const [beforeFragment, fragment] = splitAtFirst(url, '#');
  const [path, queryString] = splitAtFirst(beforeFragment, '?');
  if (
    queryString === undefined ||
    !new URLSearchParams(queryString).has(CAPABILITY_TOKEN_PARAMETER)
  ) {
    return url;
  }
  const remainingQuery = withoutTokenInQueryString(queryString);
  const querySuffix = remainingQuery ? `?${remainingQuery}` : '';
  const fragmentSuffix = fragment === undefined ? '' : `#${fragment}`;
  return `${path}${querySuffix}${fragmentSuffix}`;
}

function scrubIfString(
  value: unknown,
  scrub: (url: string) => string,
): unknown {
  return typeof value === 'string' ? scrub(value) : value;
}

function scrubQueryParams(queryParams: QueryParams): QueryParams {
  if (typeof queryParams === 'string') {
    return withoutTokenInQueryString(queryParams);
  }
  if (Array.isArray(queryParams)) {
    return queryParams.filter(([name]) => name !== CAPABILITY_TOKEN_PARAMETER);
  }
  return Object.fromEntries(
    Object.entries(queryParams).filter(
      ([name]) => name !== CAPABILITY_TOKEN_PARAMETER,
    ),
  );
}

function scrubHeaders(headers: Record<string, string>): Record<string, string> {
  return Object.fromEntries(
    Object.entries(headers)
      .filter(([name]) => !CREDENTIAL_HEADERS.includes(name.toLowerCase()))
      .map(([name, value]) =>
        name.toLowerCase() === REFERRER_HEADER
          ? [name, withoutTokenInUrl(value)]
          : [name, value],
      ),
  );
}

function withoutBodyAndCookies(request: SentryRequest): SentryRequest {
  return Object.fromEntries(
    Object.entries(request).filter(
      ([field]) => !PATIENT_DATA_REQUEST_FIELDS.includes(field),
    ),
  );
}

function scrubRequest(request: SentryRequest): SentryRequest {
  return {
    ...withoutBodyAndCookies(request),
    ...(request.url && { url: withoutTokenInUrl(request.url) }),
    ...(request.query_string && {
      query_string: scrubQueryParams(request.query_string),
    }),
    ...(request.headers && { headers: scrubHeaders(request.headers) }),
  };
}

export function scrubBreadcrumb(breadcrumb: Breadcrumb): Breadcrumb {
  const { category = '', data } = breadcrumb;
  if (!data) return breadcrumb;
  if (OUTGOING_REQUEST_BREADCRUMB_CATEGORIES.includes(category)) {
    return {
      ...breadcrumb,
      data: { ...data, url: scrubIfString(data.url, withoutQueryString) },
    };
  }
  if (category === NAVIGATION_BREADCRUMB_CATEGORY) {
    return {
      ...breadcrumb,
      data: {
        ...data,
        from: scrubIfString(data.from, withoutTokenInUrl),
        to: scrubIfString(data.to, withoutTokenInUrl),
      },
    };
  }
  return breadcrumb;
}

function scrubOutgoingRequestSpan(span: SentrySpan): void {
  if (span.op !== OUTGOING_REQUEST_SPAN_OPERATION) return;
  if (span.description) {
    span.description = withoutQueryString(span.description);
  }
  if (typeof span.data?.url === 'string') {
    span.data.url = withoutQueryString(span.data.url);
  }
}

export function scrubEvent<SentryEvent extends Event>(
  event: SentryEvent,
): SentryEvent {
  event.spans?.forEach(scrubOutgoingRequestSpan);
  return {
    ...event,
    ...(event.request && { request: scrubRequest(event.request) }),
    ...(event.breadcrumbs && {
      breadcrumbs: event.breadcrumbs.map(scrubBreadcrumb),
    }),
  };
}
